// client/constants/api.js
import axios from "axios";
import { showInfo, showWarning, showForbidden, handleError } from "../utils/toast.js";

const isProd = typeof process !== "undefined" && process.env.NODE_ENV === "production";
const METHODS_WITH_BODY = new Set(["post", "put", "patch", "delete"]);
const IDEMPOTENT = new Set(["get", "head", "options"]);

// ——— CSRF Token Management ———
let csrfToken = null;
let csrfFetchPromise = null;
/** Nach 5xx von /csrf-token: für 10s keinen erneuten Fetch (bricht Request-Stürme ab) */
let lastCsrfFail = 0;
const CSRF_BACKOFF_MS = 10_000;

async function fetchCsrfToken(baseUrl) {
  // Im Backoff nach 5xx: nicht erneut fetchen, null zurückgeben (kein Reject, damit 403-Retry nicht crasht)
  if (lastCsrfFail && (Date.now() - lastCsrfFail) < CSRF_BACKOFF_MS) {
    return null;
  }
  if (csrfFetchPromise) return csrfFetchPromise;

  csrfFetchPromise = (async () => {
    try {
      const res = await axios.get(`${baseUrl}/csrf-token`, { withCredentials: true });
      csrfToken = res.data?.csrfToken || null;
      if (csrfToken) lastCsrfFail = 0; // Bei Erfolg Backoff aufheben
      return csrfToken;
    } catch (err) {
      if (err?.response?.status >= 500) lastCsrfFail = Date.now();
      if (!isProd) console.warn("[api] CSRF token fetch failed:", err.message);
      // Bei 500: Server-Fehlerdetail anzeigen, falls CSRF_DEBUG=1 (hilft bei Fehlersuche)
      const d = err?.response?.data?.detail;
      if (d) console.error("[api] CSRF 500 – Server meldet:", d);
      return null;
    } finally {
      csrfFetchPromise = null;
    }
  })();

  return csrfFetchPromise;
}

function invalidateCsrfToken() {
  csrfToken = null;
}

/** CSRF-Token vorab laden (z. B. nach Login), damit frühe POSTs nicht 403 bekommen. */
export async function prefetchCsrfToken() {
  return fetchCsrfToken(base);
}

/** Wartet auf CSRF-Bereitschaft (für erste POSTs nach Navigation). */
export async function ensureCsrfReady() {
  if (csrfToken) return csrfToken;
  return prefetchCsrfToken();
}

export { invalidateCsrfToken };

// ——— Base URL ———
const base =
  (import.meta?.env?.VITE_API_BASE && String(import.meta.env.VITE_API_BASE).replace(/\/+$/, "")) ||
  "/api";

// ——— Axios Instanz (⚠️ KEIN globales Content-Type hier!) ———
const api = axios.create({
  baseURL: base,
  withCredentials: true,
  timeout: 60 * 60 * 1000, // 60 min
});

// ——— helpers ———
const isIdempotentRequest = (config) => IDEMPOTENT.has(String(config?.method || "get").toLowerCase());
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const backoff = (attempt) => 150 * Math.pow(2, attempt) + Math.floor(Math.random() * 120);

function buildError(error) {
  const err = error || {};
  const status = err.response?.status;
  const code = err.code || err.response?.data?.code || err.response?.data?.error?.code;
  const data = err.response?.data;
  const nestedError = data?.error && typeof data.error === "object" ? data.error : null;
  const dataMessage =
    (typeof data === "string" && data.trim()) ||
    data?.message ||
    nestedError?.message ||
    (typeof data?.error === "string" ? data.error : null) ||
    data?.msg ||
    data?.detail ||
    "";
  const requestId =
    err.response?.headers?.["x-request-id"] ||
    err.response?.headers?.["x-correlation-id"] ||
    err.config?.headers?.["X-Request-Id"];
  const e = new Error(
    dataMessage || err.message || (status ? `HTTP ${status}` : "Unbekannter Fehler")
  );
  e.status = status;
  e.code = code;
  e.data = data;
  e.requestId = requestId;
  e.config = err.config;
  return e;
}

// ——— Request Interceptor ———
api.interceptors.request.use(
  async (config) => {
    if (!config.headers) config.headers = {};
    if (!config.headers["X-Requested-With"]) config.headers["X-Requested-With"] = "XMLHttpRequest";
    if (!config.headers["Accept"]) config.headers["Accept"] = "application/json, text/plain, */*";

    const method = String(config?.method || "get").toLowerCase();
    const hasBody = METHODS_WITH_BODY.has(method);

    // ✅ CSRF-Token für state-ändernde Requests hinzufügen
    if (hasBody) {
      if (!csrfToken) {
        // Im Backoff nach CSRF-500: nicht erneut fetchen, aber auch nicht werfen –
        // ohne Token weitergehen (bei ENABLE_CSRF=0 ok), verhindert "zuerst Fehler, F5 ok"
        const inBackoff = lastCsrfFail && (Date.now() - lastCsrfFail) < CSRF_BACKOFF_MS;
        if (!inBackoff) {
          await fetchCsrfToken(base);
        }
      }
      if (csrfToken) {
        config.headers["X-CSRF-Token"] = csrfToken;
      }
    }

    // Wenn kein Body gesetzt wurde, JSON-Objekt vorbereiten (aber NICHT für FormData!)
    if (hasBody && (config.data === null || typeof config.data === "undefined")) {
      config.data = {};
    }

    // FormData erkennen
    const isFD =
      typeof FormData !== "undefined" &&
      (config.data instanceof FormData || config.data?._isFormData === true);

    if (isFD) {
      // Multipart: Content-Type vom Browser setzen lassen (Boundary!)
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];

      // Uploads dürfen länger dauern
      if (!config.timeout || config.timeout < 120000) config.timeout = 120000;
    } else if (hasBody) {
      // JSON-Bodies explizit kennzeichnen (falls nicht schon gesetzt)
      if (!config.headers["Content-Type"] && !config.headers["content-type"]) {
        config.headers["Content-Type"] = "application/json";
      }
    }

    if (!isProd) {
      // eslint-disable-next-line no-console
      console.debug("[api] →", config.method?.toUpperCase(), config.url, config.params || "", isFD ? "[FormData]" : config.data || "");
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ——— Response / Error Interceptor mit Retry ———
api.interceptors.response.use(
  (response) => {
    if (!isProd) {
      // eslint-disable-next-line no-console
      console.debug("[api] ←", response.status, response.config?.url);
    }
    return response;
  },
  async (error) => {
    if (axios.isCancel?.(error) || error?.code === "ERR_CANCELED") {
      return Promise.reject(buildError(error));
    }

    const cfg = error.config || {};
    const status = error.response?.status;
    const silent = cfg.silent === true;
    const isNetwork = !error.response && Boolean(error.message);
    const url = String(cfg?.url || "");
    // Pfad relativ zur API, z.B. "/login", "/webauthn", "/admin/system/status"
    const pathOnly = url.replace(/^https?:\/\/[^/]+/i, "");
    const isAuthPath = /^\/?(login|logout|webauthn|2fa|reset|auth)(\/|$)/i.test(pathOnly);
    const isAdminSystemPath = /^\/?admin\/system(\/|$)/i.test(pathOnly);
    // 429: Account gesperrt / Rate Limit – nie retryn, stattdessen klarer Hinweis + optionaler Countdown
    if (status === 429) {
      const err = buildError(error);
      const headers = error?.response?.headers || {};
      const retryAfter = Number(headers["retry-after"]); // Sekunden (falls gesetzt)
     const lockedUntil = err?.data?.lockedUntil || headers["x-locked-until"];
      let msg = "Zu viele Login-Versuche. Bitte kurz warten.";
      if (retryAfter && Number.isFinite(retryAfter)) {
        msg = `Zu viele Login-Versuche. Bitte ${retryAfter}s warten.`;
      } else if (lockedUntil) {
        try {
          const ms = Math.max(0, new Date(lockedUntil).getTime() - Date.now());
         const secs = Math.ceil(ms / 1000);
          if (secs > 0) msg = `Account gesperrt. Bitte ${secs}s warten.`;
        } catch {}
      }
      // gezielt über showWarning führen (kein generischer Fehler)
      showWarning(msg);
 if (isAuthPath) {
        try {
          window.dispatchEvent(new CustomEvent("login:locked", {
            detail: { retryAfter, lockedUntil }
          }));
        } catch {}
      }
      return Promise.reject(err);
    }
        // 401: Session abgelaufen — nicht mit fachlichen Codes (z. B. falscher Unterschriftscode) verwechseln
    if (status === 401) {
      const err = buildError(error);
      const data = error.response?.data;
      const errCode = String(data?.error || "").trim().toUpperCase();
      /** HTTP 401 mit fachlichem Fehlercode: Session bleibt gültig, kein Redirect Login */
      const isBusinessAuthFailure =
        errCode === "INVALID_SIGNATURE_CODE" || errCode === "INVALID_CURRENT_CODE";
      const isSessionExpired =
        !isBusinessAuthFailure &&
        (data?.unauthenticated === true ||
          !errCode ||
          errCode === "UNAUTHORIZED" ||
          errCode === "UNAUTHENTICATED" ||
          errCode === "NOT_AUTHENTICATED");
      if (!isAuthPath && !silent && isSessionExpired) {
        showInfo("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");
      }
      if (!isAuthPath && isSessionExpired) {
        try {
          window.dispatchEvent(new CustomEvent("auth:required", { detail: { path: pathOnly } }));
        } catch {}
      }
      return Promise.reject(err);
    }

    // 403: verboten oder CSRF-Fehler
    if (status === 403) {
      const err = buildError(error);
      const data = error.response?.data;
      
      // ✅ CSRF-Fehler: Token erneuern und einmal retryen
      if (data?.error === "CSRF_TOKEN_INVALID" && !cfg._csrfRetried) {
        cfg._csrfRetried = true;
        invalidateCsrfToken();
        
        // Neuen Token holen und Request wiederholen (im Backoff liefert fetchCsrfToken null)
        await fetchCsrfToken(base);
        if (csrfToken) {
          cfg.headers = cfg.headers || {};
          cfg.headers["X-CSRF-Token"] = csrfToken;
          if (!isProd) console.debug("[api] CSRF token refreshed, retrying...");
          return api(cfg);
        }
        // Kein Token (z.B. Backoff oder /csrf-token 5xx): mit Server-Meldung ablehnen
        if (!silent) {
          showWarning(data?.message || "Sicherheits-Token konnte nicht geladen werden. Bitte Seite neu laden.");
        }
        try { window.dispatchEvent(new CustomEvent("forbidden", { detail: { path: pathOnly } })); } catch {}
        return Promise.reject(err);
      }
      
      if (isAdminSystemPath && !silent) {
        showForbidden("admin_area");
      } else if (!isAuthPath && !silent) {
        // Nicht blind "Keine Berechtigung" zeigen:
        // zentrale Fehlerübersetzung berücksichtigt z. B. Upload-Limits oder konkrete Servertexte.
        // NOT_ASSIGNED-Blöcke feuern sonst parallel 5–8 Toasts → wirkt wie App-Absturz.
        const errCode = String(data?.error || data?.code || "").trim().toUpperCase();
        if (errCode === "NOT_ASSIGNED") {
          handleError(err, {
            dedupeKey: "api:not_assigned",
          });
        } else {
          handleError(err);
        }
      }
      try {
        window.dispatchEvent(new CustomEvent("forbidden", { detail: { path: pathOnly } }));
      } catch {}
      return Promise.reject(err);
    }
    // Für Auth-Routen niemals retryn
    const maxRetries =
      isAuthPath ? 0 : (typeof cfg.retry === "number" ? cfg.retry : (isIdempotentRequest(cfg) ? 3 : 0));


    cfg._attempt = cfg._attempt || 0;

    const retryable =
      maxRetries > 0 &&
      cfg._attempt < maxRetries &&
      (isNetwork || (status >= 500 && status !== 501));

    if (retryable) {
      cfg._attempt += 1;
      const wait = backoff(cfg._attempt - 1);
      if (!isProd) {
        // eslint-disable-next-line no-console
        console.warn(`[api] retry ${cfg._attempt}/${maxRetries} in ${wait}ms →`, cfg.method?.toUpperCase(), cfg.url);
      }
      await sleep(wait);
      return api(cfg);
    }

    const err = buildError(error);

    if (!silent) handleError(err);

    if (!isProd) {
      // eslint-disable-next-line no-console
      console.error("API-Fehler:", err.status, err.message, err.requestId ? `(reqId: ${err.requestId})` : "", err.data || "");
    }

    return Promise.reject(err);
  }
);

// client/constants/api.js (Auszug – PendenzenAPI)
export const PendenzenAPI = {
  listMine: (params = {}, signal) =>
    api.get("/me/pendenzen", { params, signal }).then((r) => r.data),
  list: (params = {}, signal) =>
    api.get("/pendenzen", { params, signal }).then((r) => r.data),
  create: (body) => api.post("/pendenzen", body).then((r) => r.data),
  update: (id, body) => api.patch(`/pendenzen/${id}`, body).then((r) => r.data),
  archive: (id) => api.delete(`/pendenzen/${id}`),
  deleteHard: (id) => api.delete(`/pendenzen/${id}`, { params: { hard: 1 } }),
};
 
/* =========================
 * MÄNGEL (mangelberichte)
 * ========================= */
export const MaengelAPI = {
  // Liste (mit Archiv-Filter etc.)
  list: (params = {}) =>
    api.get('/maengel', { params }).then(r => r.data),

  // Detail inkl. Positionen + Zuweisungen
  get: (id) =>
    api.get(`/maengel/${id}`).then(r => r.data), // -> { bericht, positionen }

  // Neu anlegen (erstellt automatisch verknüpften Serviceauftrag!)
  create: (body) =>
   api.post('/maengel/berichte', body).then(r => r.data), // -> { ok:true, id, service_id }

  // Update Mängelbericht (Status, Felder, Zuweisungen …)
  update: (id, body) =>
    api.put(`/maengel/${id}`, body).then(r => r.data),

  // Positionen (Bulk aus Preview übernehmen)
  upsertPositionen: (id, positionen, mode = 'merge') =>
    api.post(`/maengel/berichte/${id}/positionen`, { positionen, mode }).then(r => r.data),

  // Positionen einzeln lesen & patchen
  listPositionen: (id) =>
    api.get(`/maengel/${id}/positionen`).then(r => r.data), // -> { items: [...] }
  updatePosition: (posId, patch) =>
    api.put(`/maengel/positionen/${posId}`, patch).then(r => r.data),

  // Mehrfach-Zuweisungen
 listMitarbeiter: (id) =>
    api.get(`/maengel/${id}/mitarbeiter`).then(r => r.data), // -> { items: [{id,name}] }
  setMitarbeiter: (id, user_ids=[]) =>
    api.put(`/maengel/${id}/mitarbeiter`, { user_ids }).then(r => r.data),

  // Fotos
  listFotos: (posId) =>
   api.get(`/maengel/positionen/${posId}/fotos`).then(r => r.data),
  uploadFoto: (posId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    // Axios setzt Boundary automatisch
    return api.post(`/maengel/positionen/${posId}/fotos`, fd).then(r => r.data);
  },
  updateFoto: (fotoId, caption) =>
    api.put(`/maengel/fotos/${fotoId}`, { caption }).then(r => r.data),
  deleteFoto: (fotoId) =>
    api.delete(`/maengel/fotos/${fotoId}`).then(r => r.data),

  // Arbeit/Notizen & Messwerte
  listArbeit: (posId) =>
    api.get(`/maengel/positionen/${posId}/arbeit`).then(r => r.data),
  addArbeit: (posId, text) =>
    api.post(`/maengel/positionen/${posId}/arbeit`, { text }).then(r => r.data),
  updateArbeit: (arbeitId, text) =>
   api.put(`/maengel/arbeit/${arbeitId}`, { text }).then(r => r.data),
  deleteArbeit: (arbeitId) =>
    api.delete(`/maengel/arbeit/${arbeitId}`).then(r => r.data),

  listMesswerte: (posId) =>
    api.get(`/maengel/positionen/${posId}/messwerte`).then(r => r.data),
  addMesswert: (posId, key, value, unit=null) =>
    api.post(`/maengel/positionen/${posId}/messwerte`, { key, value, unit }).then(r => r.data),
  updateMesswert: (id, patch) =>
    api.put(`/maengel/messwerte/${id}`, patch).then(r => r.data),
  deleteMesswert: (id) =>
    api.delete(`/maengel/messwerte/${id}`).then(r => r.data),

  // Löschen (kaskadiert inkl. verknüpftem Serviceauftrag)
  delete: (id) =>
    api.delete(`/maengel/${id}`).then(r => r.data),

  patchKontroll: (id, body) =>
    api.patch(`/maengel/berichte/${id}/kontroll`, body).then((r) => r.data),

  abschliessen: (id) =>
    api.post(`/maengel/berichte/${id}/abschliessen`).then((r) => r.data),

  unterschrift: (id, body) =>
    api.post(`/maengel/berichte/${id}/unterschrift`, body).then((r) => r.data),

  unlock: (id, body = {}) =>
    api.post(`/maengel/berichte/${id}/unlock`, body).then((r) => r.data),
};

/* =========================
 * SERVICE (serviceauftraege)
 * ========================= */
export const ServiceAPI = {
  // Liste
  list: (params = {}) =>
    api.get('/service', { params }).then(r => r.data), // -> { ok:true, items:[...] }

  // Detail
  get: (id) =>
    api.get(`/service/${id}`).then(r => r.data), // -> { ok:true, item }

  // Neu
  create: (body) =>
    api.post('/service', body).then(r => r.data), // -> { ok:true, id }

  // Update (inkl. Archiv-Logik)
  update: (id, body) =>
    api.put(`/service/${id}`, body).then(r => r.data),

  // Nur Status ändern (mit Grund)
  setStatus: (id, status, grund=null) =>
    api.put(`/service/${id}/status`, { status, grund }).then(r => r.data),

  // 🏢 Transition Engine: Status-Übergang mit RBAC + Audit
  transition: (id, targetStatus, comment=null) =>
    api.post(`/service/${id}/transition`, { targetStatus, comment }).then(r => r.data),

  // 🏢 Verantwortung ändern (Projektleiter, Auftraggeber, Rechnung)
  updateVerantwortung: (id, body) =>
    api.patch(`/service/${id}/verantwortung`, body).then(r => r.data),

  // 🏢 Events/Audit-Log
  events: (id, limit=20) =>
    api.get(`/service/${id}/events`, { params: { limit } }).then(r => r.data),

  // Löschen (kaskadiert inkl. verknüpftem Mängelbericht)
  delete: (id) =>
    api.delete(`/service/${id}`).then(r => r.data),
};



export default api;